/** @jsxImportSource @emotion/react */
import { UIEvent, useContext, useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Input, Popconfirm, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Modal, ModalProps } from 'components';
import { documents, documentsQueries } from 'api';
import { ThemeContext, UserContext } from 'contexts';
import { Campaigns, Edit, MessageCircle, TrashFull } from 'icons';
import { dateSpecialFormat, format, timeSpecialFormat } from 'utils';
import { DocumentComment, DocumentCommentSource } from 'types';

import { stylesInit } from './DocumentCommentsModal.styles';

export interface DocumentCommentsModalProps extends Omit<ModalProps, 'onCancel' | 'open'> {
  documentId?: number;
  open: boolean;
  onClose: () => void;
}

const PAGE_SIZE = 10;
const SCROLL_THRESHOLD = 80;

const initialsOf = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

export const DocumentCommentsModal = ({
  documentId,
  open,
  onClose,
  ...props
}: DocumentCommentsModalProps) => {
  const { t } = useTranslation();
  const { currentTheme } = useContext(ThemeContext);
  const { data: currentUser } = useContext(UserContext);
  const styles = stylesInit(currentTheme);
  const queryClient = useQueryClient();

  const documentIdSafe = documentId ?? 0;
  const enabled = open && !!documentId;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: documentsQueries.getComments(documentIdSafe).queryKey,
    queryFn: documentsQueries.getComments(documentIdSafe, { per_page: PAGE_SIZE }).queryFn,
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
    enabled
  });

  const comments = data?.pages.flatMap((page) => page.results) ?? [];
  const count = comments.length;

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [newText, setNewText] = useState('');

  // Reset transient state when the modal closes. We intentionally do NOT use the modal's
  // destroyOnClose: unmounting the auto-sizing textarea on close triggers a null-ref crash in
  // rc-textarea's ResizeObserver (rc-textarea@1.3.2 handleResize), so we keep it mounted.
  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setEditText('');
      setNewText('');
    }
  }, [open]);

  const invalidate = () => {
    queryClient.invalidateQueries(documentsQueries.getComments(documentIdSafe).queryKey);
    queryClient.invalidateQueries(documentsQueries.getById(documentIdSafe).queryKey);
    // Also refresh the documents list so the row comment indicator (comments_count /
    // last_comments come from the list endpoint) reflects the change.
    queryClient.invalidateQueries(documentsQueries.getList().queryKey);
  };

  const { mutate: patchComment, isLoading: isSavingEdit } = useMutation(
    (variables: { id: number; text: string }) =>
      documents.patchComment(variables.id, { text: variables.text }),
    {
      onSuccess: () => {
        setEditingId(null);
        invalidate();
      }
    }
  );

  const { mutate: removeComment } = useMutation((id: number) => documents.deleteComment(id), {
    onSuccess: invalidate
  });

  const { mutate: createComment, isLoading: isPosting } = useMutation(documents.postComment, {
    onSuccess: () => {
      setNewText('');
      invalidate();
    },
    onError: (_error: AxiosError) => undefined
  });

  const canModify = (comment: DocumentComment) =>
    comment.source !== DocumentCommentSource.APOLLO &&
    !!currentUser?.id &&
    comment.user?.id === currentUser.id;

  const authorOf = (comment: DocumentComment) =>
    comment.source === DocumentCommentSource.APOLLO
      ? comment.author || comment.source
      : comment.user?.name || '';

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const sendComment = () => {
    const text = newText.trim();
    if (!text) return;
    createComment({
      text,
      documentId: documentIdSafe,
      thread_id: comments[count - 1]?.thread_id,
      response: comments[count - 1]?.response
    });
  };

  return (
    <Modal
      title={`${t('comments')} (${count}${hasNextPage ? '+' : ''})`}
      open={open}
      onCancel={onClose}
      footer={null}
      className="comments-modal"
      width={680}
      {...props}
    >
      <div css={styles.body}>
        {isLoading ? (
          <div css={styles.state}>
            <Spin />
          </div>
        ) : count === 0 ? (
          <div css={styles.state}>
            <MessageCircle />
            <span>{t('noComments')}</span>
          </div>
        ) : (
          <div css={styles.list} onScroll={onScroll}>
            {comments.map((comment) => (
              <div css={styles.item} key={comment.id}>
                <Avatar css={styles.avatar} size={36} src={comment.user?.logo || undefined}>
                  {initialsOf(authorOf(comment))}
                </Avatar>
                <div css={styles.content}>
                  <div css={styles.head}>
                    <span css={styles.author}>{authorOf(comment)}</span>
                    <span css={styles.date}>
                      {format(comment.created_at, dateSpecialFormat)} ·{' '}
                      {format(comment.created_at, timeSpecialFormat)}
                    </span>
                    {canModify(comment) && editingId !== comment.id && (
                      <div css={styles.actions}>
                        <button
                          type="button"
                          className="edit"
                          aria-label={t('edit')}
                          onClick={() => {
                            setEditingId(comment.id);
                            setEditText(comment.text ?? '');
                          }}
                        >
                          <Edit />
                        </button>
                        <Popconfirm
                          title={t('deleteCommentConfirm')}
                          okText={t('confirm')}
                          cancelText={t('cancel')}
                          onConfirm={() => removeComment(comment.id)}
                        >
                          <button type="button" className="danger" aria-label={t('delete')}>
                            <TrashFull />
                          </button>
                        </Popconfirm>
                      </div>
                    )}
                  </div>

                  {editingId === comment.id ? (
                    <div css={styles.edit}>
                      <Input.TextArea
                        autoSize={{ minRows: 1, maxRows: 6 }}
                        value={editText}
                        onChange={(event) => setEditText(event.target.value)}
                      />
                      <div css={styles.editButtons}>
                        <Button size="small" onClick={() => setEditingId(null)}>
                          {t('cancel')}
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          loading={isSavingEdit}
                          disabled={!editText.trim()}
                          onClick={() => patchComment({ id: comment.id, text: editText.trim() })}
                        >
                          {t('save')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div css={styles.text}>{comment.text}</div>
                  )}
                </div>
              </div>
            ))}
            {isFetchingNextPage && (
              <div css={styles.state}>
                <Spin />
              </div>
            )}
          </div>
        )}

        <div css={styles.composer}>
          <Input.TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            placeholder={t('writeComment')}
            value={newText}
            onChange={(event) => setNewText(event.target.value)}
            onPressEnter={(event) => {
              if (!event.shiftKey) {
                event.preventDefault();
                sendComment();
              }
            }}
          />
          <Button
            type="primary"
            icon={<Campaigns />}
            loading={isPosting}
            disabled={!newText.trim()}
            onClick={sendComment}
          />
        </div>
      </div>
    </Modal>
  );
};
